import React from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { Bars3Icon } from '@heroicons/react/24/outline';
import type { components } from '../../../../types/learnerApi';

type Config = components['schemas']['SanitizedOrderingConfig'];

export interface OrderingAnswer {
  ordered_ids: string[];
}

export interface OrderingSequenceViewProps {
  item: { id: string; points?: number; config: Config };
  mode: 'learner' | 'preview';
  value?: OrderingAnswer;
  onChange?: (value: OrderingAnswer) => void;
  disabled?: boolean;
}

interface DragItemProps {
  id: string;
  text: string;
  disabled?: boolean;
}

const DragItem: React.FC<DragItemProps> = ({ id, text, disabled }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={id}
      dragListener={false}
      dragControls={controls}
      className={`flex items-center gap-3 px-3 py-3 rounded-lg border border-border bg-surface-secondary select-none
        ${disabled ? 'opacity-60' : ''}
      `}
    >
      <button
        type="button"
        onPointerDown={(e) => !disabled && controls.start(e)}
        className={`text-text-tertiary flex-shrink-0 ${disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
        aria-label="Drag handle"
      >
        <Bars3Icon className="w-5 h-5" />
      </button>
      <span className="text-sm text-text-primary">{text}</span>
    </Reorder.Item>
  );
};

const OrderingSequenceView: React.FC<OrderingSequenceViewProps> = ({
  item,
  mode,
  value,
  onChange,
  disabled,
}) => {
  // Use value.ordered_ids to determine display order; fall back to server-returned order
  const serverItems = item.config.items;
  const orderedIds = value?.ordered_ids ?? serverItems.map((it) => it.id);

  const itemMap = React.useMemo(
    () => new Map(serverItems.map((it) => [it.id, it.text])),
    [serverItems]
  );

  const handleReorder = (newIds: string[]) => {
    if (disabled || mode === 'preview') return;
    onChange?.({ ordered_ids: newIds });
  };

  return (
    <div className="space-y-4">
      <p className="font-medium text-text-primary">{item.config.question}</p>
      <Reorder.Group
        axis="y"
        values={orderedIds}
        onReorder={handleReorder}
        className="space-y-2"
      >
        {orderedIds.map((id) => (
          <DragItem
            key={id}
            id={id}
            text={itemMap.get(id) ?? id}
            disabled={disabled || mode === 'preview'}
          />
        ))}
      </Reorder.Group>
      {item.points != null && (
        <p className="text-xs text-text-tertiary">{item.points} pt{item.points !== 1 ? 's' : ''}</p>
      )}
    </div>
  );
};

export default OrderingSequenceView;
